<template>
    <div class="text-sm rounded-lg relative overflow-hidden">
        <transition name="fade">
            <div v-if="loading"
                class="absolute inset-0 bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm z-10 flex items-center justify-center rounded-lg">
                <svg aria-hidden="true" class="w-10 h-10 text-gray-200 animate-spin dark:text-neutral-600 fill-primary"
                    viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor" />
                    <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill" />
                </svg>
            </div>
        </transition>

        <transition name="fade">
            <div v-if="sent"
                class="absolute inset-0 bg-white dark:bg-neutral-800 z-10 flex flex-col items-center justify-center rounded-lg gap-4">
                <div class="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                    <svg class="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                </div>
                <p class="text-lg font-semibold dark:text-white">{{ $t('Message sent') }}!</p>
                <p class="text-sm text-gray-400 dark:text-neutral-400 text-center px-4">{{ $t("We will get back to you as soon as possible") }}.</p>
                <div @click="reset" class="mt-2 text-xs text-primary cursor-pointer hover:underline">{{ $t('Send another message') }}</div>
            </div>
        </transition>

        <transition name="slide-down">
            <div v-if="error"
                class="mb-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-xs flex items-center gap-2">
                <svg class="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clip-rule="evenodd" />
                </svg>
                {{ error }}
            </div>
        </transition>

        <div class="font-bold mb-1 dark:text-white">{{ $t('More info') }}?</div>

        <div class="w-full max-w-2xl mx-auto space-y-3">
            <input v-model="form.name"
                class="w-full appearance-none placeholder-gray-500 dark:placeholder-gray-300 bg-gray-200 dark:bg-neutral-900 text-gray-700 dark:text-white border border-gray-200 dark:border-neutral-300/10 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white dark:focus:bg-neutral-900 focus:border-primary"
                :placeholder="$t('Name')" type="text" />
            <input v-model="form.email"
                class="w-full appearance-none placeholder-gray-500 dark:placeholder-gray-300 bg-gray-200 dark:bg-neutral-900 text-gray-700 dark:text-white border border-gray-200 dark:border-neutral-300/10 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white dark:focus:bg-neutral-900 focus:border-primary"
                :placeholder="$t('Your e-mail')" type="email" />
            <textarea v-model="form.message"
                class="w-full appearance-none placeholder-gray-500 dark:placeholder-gray-300 bg-gray-200 dark:bg-neutral-900 text-gray-700 dark:text-white border border-gray-200 dark:border-neutral-300/10 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white dark:focus:bg-neutral-900 focus:border-primary"
                :placeholder="$t('Type your message here')" rows="4"></textarea>

            <div @click="submit"
                class="block w-full bg-gradient shadow-xl px-5 py-5 rounded-lg cursor-pointer hover:scale-95 transition duration-300 active:scale-75 text-white text-center select-none"
                :class="loading ? 'opacity-50 pointer-events-none' : ''">
                {{ submitLabel }}
            </div>
        </div>

    </div>
</template>
  
<script>
export default {
    name: 'AskAQuestion',

    props: {
        accomodation: { type: String, default: '' },
        accoUuid: { type: String, default: '' },
        submitButtonText: { type: [Object, String], default: null },
    },

    data() {
        return {
            loading: false,
            sent: false,
            error: null,
            form: {
                name: '',
                email: '',
                message: '',
            },
            csrf: document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        }
    },

    computed: {
        submitLabel() {
            if (!this.submitButtonText) return this.$t('send')
            if (typeof this.submitButtonText === 'object') {
                const lang = document.documentElement.lang
                return this.submitButtonText[lang] || this.submitButtonText['en'] || this.$t('send')
            }
            return this.submitButtonText
        },
    },

    methods: {
        async submit() {
            this.error = null

            if (!this.form.name || !this.form.email || !this.form.message) {
                this.error = this.$t('Please fill in all fields.')
                return
            }

            this.loading = true

            try {
                const body = new FormData()
                body.append('_token', this.csrf)
                body.append('accomodation', this.accomodation)
                body.append('acco_uuid', this.accoUuid)
                body.append('name', this.form.name)
                body.append('email', this.form.email)
                body.append('message', this.form.message)

                const response = await fetch('/ask', {
                    method: 'POST',
                    body,
                })

                if (!response.ok) throw new Error()

                if (this.formSubmitReturnUrl) {
                    window.open(this.formSubmitReturnUrl, '_blank')
                }

                this.sent = true
            } catch (e) {
                this.error = this.$t('Something went wrong. Please try again.')
            } finally {
                this.loading = false
            }
        },

        reset() {
            this.sent = false
            this.error = null
            this.form = { name: '', email: '', message: '' }
        },
    },
}
</script>
  
<style scoped>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s ease;
}

.slide-down-enter,
.slide-down-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>